import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import type { NodeKey } from "lexical";
import { type FC, useCallback, useLayoutEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useToken } from "../../../../../../server/auth/use-token";
import { serviceURL } from "../../../../../../utils/service-url";
import { uuidv4 } from "../../../../../../utils/uuid";
import { AssetPicker } from "../../../../../assets/components/asset-picker";
import type { Asset } from "../../../../../assets/server/digital-asset-service";
import { usePublishDesignHuddleTemplateWithTags } from "../../../../../design-huddle/use-publish-template-with-tags";
import { copyImage } from "../../../../../design-huddle/utils";
import { useContentTypeMultipage } from "../../../../../multiformat/hooks/use-content-type-multipage";
import { AssetPreview } from "./asset-preview";
import { ContentLibraryModalAddNew } from "./content-library-modal-add-new";
import { InsertImageButton } from "./insert-image-button";
import { InsertAsLinkButton } from "./insert-link-button";
import { InsertVideoButton } from "./insert-video-button";

export const ContentLibraryModal: FC<{
  open: boolean;
  onClose: () => void;
  assets: Asset[];
  nodeKey?: NodeKey;
  onUseAsLink?: (url: string, title: string) => void;
}> = ({ open, onClose, assets, nodeKey, onUseAsLink }) => {
  const { t } = useTranslation();
  const getToken = useToken();
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { data: multipage } = useContentTypeMultipage(selectedAsset?.content_type);
  const publishTags = usePublishDesignHuddleTemplateWithTags();

  const handleUseAssetAsLink = useCallback(async () => {
    if (!onUseAsLink || !selectedAsset) return;

    setLoading(true);
    if (!selectedAsset.public_url) {
      setLoading(false);
      return null;
    }

    if (selectedAsset.content_type === "Uploaded File") {
      onUseAsLink(selectedAsset.public_url, selectedAsset.title);
      onClose();
      setLoading(false);
      return;
    }

    const template = selectedAsset.project_id && (await publishTags(selectedAsset.project_id));
    if (template) {
      const params =
        template.mergeTags.length > 0
          ? `?${template.mergeTags.map((tag) => `${tag}={{${tag}}}`).join("&")}`
          : "";
      const templateUrl = `${serviceURL("huddle")}template/${template.templateCode}/preview${params}`;
      onUseAsLink(templateUrl, selectedAsset.title);
      onClose();
      setLoading(false);
      return;
    }

    const response = await copyImage(getToken, selectedAsset.public_url, uuidv4());
    if (!response.success) {
      toast.error("Failed to use image as link");
      setLoading(false);
      return null;
    }

    onUseAsLink(response.url, selectedAsset.title);
    onClose();
    setLoading(false);
  }, [getToken, onClose, onUseAsLink, selectedAsset, publishTags]);

  // set initial modal height so that it is retained after filtering is applied
  useLayoutEffect(() => {
    if (open) {
      setTimeout(() => {
        if (contentRef.current && contentHeight === null) {
          setContentHeight(contentRef.current.offsetHeight);
        }
      }, 100);
    }
  }, [contentHeight, open]);

  if (assets.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth={"lg"}>
        <DialogTitle mt={2}>{t("Insert")}</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            {t("You haven't saved any content yet. Visit your Content Library to do so.")}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="editor-content-empty-library-cancel"
            type="button"
            onClick={onClose}
            loading={loading}
          >
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth={previewAsset ? "md" : "lg"}>
      {previewAsset ? (
        <>
          <DialogTitle mt={2}>{t("Insert")}</DialogTitle>
          <Box ml={2}>
            <Button
              startIcon={<ArrowBack />}
              data-analytics-id="editor-content-library-back"
              onClick={() => setPreviewAsset(null)}
            >
              {t("Back")}
            </Button>
          </Box>
          <DialogContent>
            <Box mb={2}>
              <Typography variant="h6">{previewAsset.title}</Typography>
            </Box>
            <AssetPreview
              asset={previewAsset}
              height="300px"
              iconFontSize={150}
              showPreviewIcon={false}
              videoPlayer={true}
            />
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle my={2}>{t("Content Library")}</DialogTitle>
          <DialogContent
            ref={contentRef}
            sx={{ height: contentHeight ? `${contentHeight}px` : "auto", overflowY: "auto" }}
          >
            <AssetPicker
              assets={assets}
              setPreviewAsset={setPreviewAsset}
              selectedAsset={selectedAsset}
              setSelectedAsset={setSelectedAsset}
            >
              <ContentLibraryModalAddNew onClose={onClose} />
            </AssetPicker>
          </DialogContent>
        </>
      )}
      <DialogActions>
        <Button
          data-analytics-id="editor-content-library-cancel"
          type="button"
          onClick={onClose}
          loading={loading}
        >
          {t("Cancel")}
        </Button>
        {previewAsset && (
          <InsertAsLinkButton
            asset={selectedAsset}
            onClose={onClose}
            loading={loading}
            setLoading={setLoading}
          />
        )}
        {previewAsset && !onUseAsLink && !multipage && (
          <InsertImageButton
            buttonText={t("Insert image")}
            asset={selectedAsset}
            onClose={onClose}
            loading={loading}
            setLoading={setLoading}
            nodeKey={nodeKey}
            onInsert={() =>
              selectedAsset
                ? new Promise((resolve) => resolve({ success: true, id: selectedAsset.id }))
                : Promise.resolve({ success: false })
            }
          />
        )}
        {previewAsset && !onUseAsLink && (
          <InsertVideoButton
            asset={selectedAsset}
            onClose={onClose}
            loading={loading}
            setLoading={setLoading}
          />
        )}
        {!previewAsset && !onUseAsLink && (
          <Button
            data-analytics-id="editor-content-library-preview-image"
            type="button"
            disabled={selectedAsset === null}
            onClick={() => {
              setPreviewAsset(selectedAsset);
            }}
          >
            {t("Insert")}
          </Button>
        )}
        {onUseAsLink && (
          <Button
            data-analytics-id="editor-content-library-use-link"
            type="button"
            disabled={selectedAsset === null}
            loading={loading}
            onClick={() => {
              if (selectedAsset) {
                void handleUseAssetAsLink();
              }
            }}
          >
            {t("Use as link")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
